import { finiteCoastal as finite, hasCoastalEvidence } from './coastal-evidence';
import type { MarineHourly, NormalisedHour } from "../daily-obs-types";
import type {
  CoastalLocationConfig,
  CoastalProfile,
  CoastalSceneState,
  SwellComponent,
  TideInput,
} from "./coastal-types";

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function signedAngle(directionDeg: number, referenceDeg: number | null): number {
  if (!finite(referenceDeg) || !finite(directionDeg)) return 0;
  return (((directionDeg - referenceDeg) % 360 + 540) % 360) - 180;
}

function completeComponent(
  kind: SwellComponent["kind"],
  heightM: number | null,
  periodS: number | null,
  directionDeg: number | null,
  facingDeg: number | null,
): SwellComponent | null {
  if (!finite(heightM) || !finite(periodS) || !finite(directionDeg) || heightM <= 0 || periodS <= 0) {
    return null;
  }
  return {
    kind,
    heightM,
    periodS,
    directionDeg,
    shoreRelativeDeg: signedAngle(directionDeg, facingDeg),
  };
}

function buildSwells(marine: MarineHourly, facingDeg: number | null): SwellComponent[] {
  const primary = completeComponent(
    "primary",
    marine.swell_height_m,
    marine.swell_period_s,
    marine.swell_dir_deg,
    facingDeg,
  ) ?? completeComponent(
    "primary",
    marine.wave_height_m,
    marine.wave_period_s,
    marine.wave_dir_deg,
    facingDeg,
  );

  return [
    primary,
    completeComponent("secondary", marine.swell2_height_m, marine.swell2_period_s, marine.swell2_dir_deg, facingDeg),
    completeComponent("tertiary", marine.swell3_height_m, marine.swell3_period_s, marine.swell3_dir_deg, facingDeg),
    completeComponent("wind-wave", marine.wind_wave_height_m, marine.wind_wave_period_s, marine.wind_wave_dir_deg, facingDeg),
  ].filter((component): component is SwellComponent => component !== null);
}

function normaliseLevel(level: number, values: readonly number[]): number {
  const min = Math.min(...values), max = Math.max(...values);
  return max - min < 0.001 ? 0.5 : clamp01((level - min) / (max - min));
}

function deriveTide(input: TideInput, current: NormalisedHour): CoastalSceneState['tide'] {
  const points = (input.points ?? []).slice(0, 49).filter((p) => p && finite(p.hour) && finite(p.heightM) && p.hour >= 0 && p.hour <= 24)
    .sort((a, b) => a.hour - b.hour);
  const before = points.filter((p) => p.hour <= current.hour).at(-1);
  const after = points.find((p) => p.hour >= current.hour);
  if (before && after && points.length >= 2) {
    const fraction = after.hour === before.hour ? 0 : (current.hour - before.hour) / (after.hour - before.hour);
    const heightM = before.heightM + (after.heightM - before.heightM) * fraction;
    const levels = points.map((p) => p.heightM);
    return { heightM, position01: normaliseLevel(heightM, levels), rangeM: Math.max(...levels) - Math.min(...levels),
      state: before === after ? before.state : after.heightM >= before.heightM ? 'rising' : 'falling', source: 'curve' };
  }
  const valid = input.dayHours.slice(0, 48).filter((h) => h && finite(h.hour) && finite(h.marine?.sea_level_height_m))
    .sort((a, b) => a.hour - b.hour);
  const values = valid.map((h) => h.marine!.sea_level_height_m!);
  const heightM = current.marine?.sea_level_height_m;
  if (finite(heightM) && values.length >= 2 && Math.max(...values) - Math.min(...values) >= 0.001) {
    const previous = valid.filter((h) => h.hour < current.hour).at(-1)?.marine?.sea_level_height_m;
    const next = valid.find((h) => h.hour > current.hour)?.marine?.sea_level_height_m;
    const position01 = normaliseLevel(heightM, values);
    const rangeM = Math.max(...values) - Math.min(...values);
    const rising = finite(next) ? next >= heightM : finite(previous) ? heightM >= previous : true;
    return { heightM, position01, rangeM, state: rangeM > 0.01 && position01 >= 0.98 ? 'high'
      : rangeM > 0.01 && position01 <= 0.02 ? 'low' : rising ? 'rising' : 'falling', source: 'marine' };
  }
  return { heightM: null, position01: 0.5, state: 'unavailable', source: 'neutral' };
}

export function coastalSmoothness(height: number, windWave: number | null, wind: number, gust: number, shoreAngle: number | null): number {
  const chop = finite(windWave) && height > 0 ? Math.max(0, windWave) / height : 0;
  const onshore = shoreAngle === null ? 0.5 : (1 + Math.cos(shoreAngle * Math.PI / 180)) / 2;
  const windPenalty = Math.max(0, wind - 12) / 50 * (0.65 + onshore * 0.55);
  return clamp01(1 - chop * 0.8 - windPenalty - Math.max(0, gust - wind - 15) / 100);
}

export function buildCoastalSceneState(input: {
  location: CoastalLocationConfig;
  profile: CoastalProfile;
  dateKey: string;
  hour: NormalisedHour;
  tide: TideInput;
  waterQuality?: { secchiDepthM: number | null; chlorophyllMgM3: number | null } | null;
}): CoastalSceneState {
  const { hour, location } = input;
  const marine = hour.marine;
  const weather: Partial<NormalisedHour["weather"]> = hour.weather ?? {};
  const number = (value: unknown, fallback = 0) => finite(value) ? value : fallback;
  const optional = (value: unknown) => finite(value) ? value : null;
  const swells = marine ? buildSwells(marine, location.beachFacingDeg) : [];
  const combinedHeight = Math.max(0, number(marine?.wave_height_m, swells[0]?.heightM ?? 0));
  const combinedPeriod = Math.max(0, number(marine?.wave_period_s, swells[0]?.periodS ?? 0));
  const windSpeed = Math.max(0, number(weather.wind_speed_10m_kmh));
  const windGust = Math.max(windSpeed, number(weather.wind_gust_10m_kmh));
  const shoreAngle = finite(weather.wind_dir_10m_deg) && finite(location.beachFacingDeg)
    ? signedAngle(weather.wind_dir_10m_deg, location.beachFacingDeg) : null;
  const crossSwell = swells.filter((s) => s.kind === 'secondary' || s.kind === 'tertiary').reduce((sum, swell) =>
    sum + swell.heightM / Math.max(0.1, combinedHeight) * Math.abs(Math.sin((swell.directionDeg - (swells[0]?.directionDeg ?? swell.directionDeg)) * Math.PI / 180)) * 0.25, 0);

  return {
    availability: hasCoastalEvidence(hour) ? "ready" : "missing-marine",
    breakerStyle: input.profile.breakerStyle,
    seed: `${location.slug}:${input.dateKey}`,
    hour: Math.max(0, Math.min(23, number(hour.hour, 12))),
    swells,
    combinedWaveHeightM: combinedHeight,
    combinedPeriodS: combinedPeriod,
    cleanliness01: clamp01(coastalSmoothness(combinedHeight, optional(marine?.wind_wave_height_m), windSpeed, windGust, shoreAngle) - crossSwell),
    tide: deriveTide(input.tide, hour),
    wind: {
      speedKmh: windSpeed,
      gustKmh: windGust,
      shoreRelativeDeg: shoreAngle ?? 90,
    },
    weather: {
      daylight: weather.is_day === true,
      cloud01: clamp01(number(weather.cloud_cover_pct, 50) / 100),
      rainMm: Math.max(0, number(weather.rain_mm, number(weather.precip_mm))),
      visibilityM: optional(weather.visibility_m),
      temperatureC: number(weather.temp_c),
      weatherCode: number(weather.weather_code),
    },
    water: {
      sstC: optional(marine?.sst_c),
      secchiDepthM: optional(input.waterQuality?.secchiDepthM),
      chlorophyllMgM3: optional(input.waterQuality?.chlorophyllMgM3),
    },
  };
}

export function describeCoastalScene(state: CoastalSceneState): string {
  if (state.availability === "missing-marine") {
    return "Coastal conditions visualisation unavailable because marine data is missing.";
  }
  const cadence = state.combinedPeriodS >= 12
    ? "long-period sets"
    : state.combinedPeriodS <= 7
      ? "short-period waves"
      : "regular swell";
  const surface = state.cleanliness01 >= 0.7 ? "smooth wave faces" : state.cleanliness01 <= 0.35 ? "messy wind chop" : "mixed surface texture";
  const tide = state.tide.state === "unavailable" ? "tide unavailable" : `${state.tide.state} tide`;
  const weather = state.weather.rainMm > 0 ? "with rain" : state.weather.cloud01 > 0.75 ? "under overcast skies" : "under open skies";
  return `Representative coastal conditions visualisation showing ${state.combinedWaveHeightM.toFixed(1)} metre ${cadence}, ${surface}, ${tide}, ${weather}.`;
}
